// {{key}} substitution + the catalog of placeholders the editor can offer.
import { TRANSACTION_TEMPLATE_META } from "./transactionEmailSequence";

const TRANSACTION_IDS = Object.keys(TRANSACTION_TEMPLATE_META);

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

// Time-aware greeting for {{greeting}}: morning until noon, afternoon until
// 5 PM, evening after. Hour is injected so tests don't depend on the clock.
export function greetingForHour(hour) {
  const h = Number(hour);
  if (!Number.isFinite(h) || h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

// Literal greetings become the time-aware {{greeting}} token, so a template
// typed or pasted with "Good Morning/Afternoon" adapts to the hour it's sent
// (Henry, 2026-09-01: "when we make a templete it knows if its morning or
// afternoon"). Two rules keep it safe:
//   1. The slash placeholder forms (Good Morning/Afternoon[/Evening]) are
//      unambiguous — replaced wherever they appear.
//   2. A single "Good Morning|Afternoon|Evening" is replaced only at the very
//      start of the text, where it can only be a salutation — "have a good
//      morning" mid-sentence stays put.
export function normalizeGreetings(text) {
  if (!text) return "";
  return String(text)
    .replace(/good\s+morning\s*\/\s*afternoon(\s*\/\s*evening)?/gi, "{{greeting}}")
    .replace(/^(\s*)good\s+(morning|afternoon|evening)/i, "$1{{greeting}}");
}

export function interpolate(template, data) {
  if (template === null || template === undefined) return "";
  const noData = data === null || data === undefined;
  const d = noData ? {} : data;
  return String(template).replace(PLACEHOLDER_RE, (whole, key) => {
    if (noData) return "";
    if (!Object.prototype.hasOwnProperty.call(d, key)) return whole;
    const v = d[key];
    return v === null || v === undefined ? "" : String(v);
  });
}

// Groups carry an optional `templates` whitelist — only those templates see
// the group. When `templates` is missing, the group shows everywhere.
export const PLACEHOLDER_GROUPS = [
  {
    label: "Transaction",
    templates: TRANSACTION_IDS,
    items: [
      { key: "greeting",        display: "Greeting (time-aware)", icon: "🕐" },
      { key: "propertyAddress", display: "Property Address", icon: "📍" },
      { key: "clientNames",     display: "Client names",     icon: "👥" },
      { key: "sellerNames",     display: "Seller names",     icon: "👤" },
      { key: "buyerNames",      display: "Buyer names",      icon: "👤" },
      { key: "agentName",       display: "Agent name",       icon: "✍️" },
    ],
  },
  {
    label: "Open House",
    templates: [
      "scheduling-seller", "scheduling-agent",
      "update-seller", "update-agent",
      "cancellation-seller", "cancellation-agent",
      "recap-seller",
    ],
    items: [
      { key: "propertyAddress", display: "Property Address", icon: "📍" },
      { key: "date",             display: "Date",             icon: "🗓" },
      { key: "time",             display: "Time",             icon: "⏱" },
    ],
  },
  {
    label: "Seller",
    templates: [
      "scheduling-seller", "update-seller", "cancellation-seller",
      "recap-seller",
    ],
    items: [
      { key: "sellerFirstName", display: "Seller first name", icon: "👤" },
      { key: "sellerName",      display: "Seller full name",  icon: "👤" },
    ],
  },
  {
    label: "Hosting Agent",
    templates: [
      "scheduling-seller", "scheduling-agent",
      "update-seller", "update-agent",
      "cancellation-seller", "cancellation-agent",
    ],
    items: [
      { key: "hostingAgentFirstName", display: "Hosting agent first name", icon: "🧑" },
      { key: "hostingAgentName",      display: "Hosting agent full name",  icon: "🧑" },
    ],
  },
  {
    label: "Feedback",
    templates: ["recap-seller"],
    items: [
      { key: "groups",         display: "# of groups",        icon: "👥" },
      { key: "secondShowings", display: "# of 2nd showings",  icon: "🔁" },
      { key: "agentFeedback",  display: "Agent feedback",     icon: "💬" },
      { key: "buyerFeedback",  display: "Buyer feedback",     icon: "💬" },
      { key: "otherComments",  display: "Other comments",     icon: "💬" },
    ],
  },
  // ===== Client touchpoints + marketing templates =====
  {
    label: "Contact",
    templates: [
      "birthday", "home-anniversary", "closing-day", "welcome",
      "just-listed", "just-sold",
    ],
    items: [
      { key: "contactFirstName", display: "Contact first name", icon: "👤" },
      { key: "contactName",      display: "Contact full name",  icon: "👤" },
    ],
  },
  {
    label: "Property",
    templates: [
      "home-anniversary", "closing-day", "just-listed", "just-sold",
    ],
    items: [
      { key: "propertyAddress", display: "Property Address", icon: "📍" },
    ],
  },
  {
    label: "Anniversary",
    templates: ["home-anniversary"],
    items: [
      { key: "yearsInHome", display: "Years in home", icon: "🎉" },
    ],
  },
  {
    label: "Sender",
    templates: [
      "birthday", "home-anniversary", "closing-day", "welcome",
      "just-listed", "just-sold",
      "staging-consult-vendor", "staging-install-vendor", "staging-destage-vendor",
      "photographer-vendor", "pre-inspection-vendor",
    ],
    items: [
      { key: "senderFirstName", display: "Your first name", icon: "✍️" },
      { key: "senderName",      display: "Your full name",  icon: "✍️" },
    ],
  },
  // ===== Vendor Notifications =====
  {
    label: "Vendor",
    templates: [
      "staging-consult-vendor", "staging-install-vendor", "staging-destage-vendor",
      "photographer-vendor", "pre-inspection-vendor",
    ],
    items: [
      { key: "vendorName", display: "Vendor name", icon: "🏠" },
    ],
  },
  {
    label: "Deal",
    templates: [
      "staging-consult-vendor", "staging-install-vendor", "staging-destage-vendor",
      "photographer-vendor", "pre-inspection-vendor",
    ],
    items: [
      { key: "propertyAddress", display: "Property Address", icon: "📍" },
      { key: "clients",          display: "Clients",         icon: "👥" },
      { key: "closingDate",      display: "Closing Date",    icon: "🗓" },
      { key: "targetListDate",   display: "Target List Date", icon: "🗓" },
    ],
  },
  {
    label: "Schedule",
    templates: ["staging-install-vendor"],
    items: [
      { key: "installDate", display: "Install Date", icon: "🗓" },
    ],
  },
  {
    label: "Schedule",
    templates: ["staging-destage-vendor"],
    items: [
      { key: "destageDate", display: "Destage Date", icon: "🗓" },
    ],
  },
  {
    label: "Schedule",
    templates: ["photographer-vendor"],
    items: [
      { key: "photoshootDate", display: "Photoshoot Date", icon: "🗓" },
    ],
  },
  {
    label: "Inspections",
    templates: ["pre-inspection-vendor"],
    items: [
      { key: "inspectionList", display: "Inspection list", icon: "📋" },
    ],
  },
  {
    label: "Lockbox",
    templates: [
      "staging-consult-vendor", "staging-install-vendor", "staging-destage-vendor",
      "photographer-vendor", "pre-inspection-vendor",
    ],
    items: [
      { key: "lockbox",       display: "Lockbox (auto)", icon: "🔐" },
      { key: "lockboxType",   display: "Lockbox type",   icon: "🔐" },
      { key: "lockboxNumber", display: "Lockbox number", icon: "🔢" },
      { key: "lockboxCode",   display: "Lockbox code",   icon: "🔑" },
    ],
  },
];

export function placeholdersFor(templateId) {
  // Custom (user-created) templates get the general Transaction placeholder set
  // (greeting, address, client/seller/buyer names, agent) plus any un-scoped groups.
  const isCustom = typeof templateId === "string" && templateId.startsWith("custom-");
  return PLACEHOLDER_GROUPS.filter(
    (g) =>
      !g.templates ||
      g.templates.includes(templateId) ||
      (isCustom && g.label === "Transaction"),
  );
}
